<template>
	<div class="third-measure-table">
		<el-form v-if="rows.length" label-position="top" size="small" class="third-measure-form">
			<!-- section 为按套装部件拆出的测量板块，无 suiteComponents 时只有一个无标题分组。 -->
			<div v-for="(section, sectionIndex) in sections" :key="'section:' + sectionIndex" class="third-measure-section">
				<div v-if="section.title" class="third-measure-section-title">{{ section.title }}</div>
				<el-row type="flex" :gutter="32" class="third-measure-grid">
					<!-- cell 对应原配置下标，组合 key 避免套装中的重复编码冲突。 -->
					<el-col v-for="cell in section.cells" :key="cell.row.code + ':' + cell.index" :xs="24" :sm="24" :md="24"
						:lg="columns === 1 ? 24 : 12">
						<el-form-item :label="cell.row.name" :required="cell.row.required"
							:error="showErrors ? errors[cell.index] : ''">
							<ThirdMeasureControl :config="items[cell.index]" :value="cell.row" :disabled="disabled"
								@input="changeRow(cell.index, $event)" />
						</el-form-item>
					</el-col>
				</el-row>
			</div>
		</el-form>
		<el-empty v-else :image-size="72" description="请选择商品分类后填写测量结果" />
	</div>
</template>

<script>
// 按配置递归渲染下拉框及其选中分支。
import ThirdMeasureControl from "./ThirdMeasureControl.vue";
// 统一处理回显、空值过滤、套装分组和提交校验。
import { createMeasureResults, getMeasureError, groupMeasureItems, hasMeasureValue } from "./thirdMeasure";

export default {
	name: "ThirdMeasureTable",
	components: { ThirdMeasureControl },
	props: {
		// 宽屏展示列数；窄卡片传 1，新品检查页默认使用双列。
		columns: { type: Number, default: 2 },
		// 当前商品分类下的测量项配置。
		items: { type: Array, default: () => [] },
		// 通过 v-model 接收回显数据，并向父级同步最新结果。
		value: { type: Array, default: () => [] },
		// 当前批次包是否禁止测量。
		disabled: { type: Boolean, default: false },
	},
	// 保存提交后是否展示校验信息的状态。
	data() {
		return {
			// 首次提交前不打断用户填写。
			showErrors: false,
		};
	},
	computed: {
		// 配置与回显可以先后到达，始终按测量编码匹配而非数组位置。
		rows() {
			return createMeasureResults(this.items, this.value);
		},
		// 有套装部件时按标题分板块，编辑和提交仍使用原配置下标。
		sections() {
			return groupMeasureItems(this.items).map((group) => ({
				title: group.title,
				cells: group.indexes.map((index) => ({
					index,
					row: this.rows[index],
				})),
			}));
		},
		// 各测量项的校验结果，修改数值后自动更新。
		errors() {
			return this.rows.map((row, index) => getMeasureError(this.items[index], row));
		},
	},
	methods: {
		// index 为当前行下标，value 为控件发出的完整结果；父级立即获得最新数据。
		changeRow(index, value) {
			// 使用新数组触发 Vue 2 的响应式更新，不修改 props。
			const rows = this.rows.slice();
			rows[index] = value;
			this.$emit("input", rows);
		},
		// 提供可提交的 DTO 列表，也可由父级通过 ref 主动读取。
		getResult() {
			return this.rows.filter(hasMeasureValue);
		},
		// 校验当前分类及测量值，供单次提交和批量复制复用。
		validate() {
			this.showErrors = true;
			// 优先展示具体测量项的问题，再提示尚未填写测量结果。
			const error = this.errors.find((message) => message) ||
				(!this.getResult().length ? "请先填写测量结果" : "");
			if (error) this.$message.warning(error);
			return !error;
		},
	},
};
</script>

<style scoped lang="less">
.third-measure-table {
	width: 100%;
	text-align: left;
}
.third-measure-form {
	::v-deep .el-form-item {
		margin-bottom: 24px;
	}
	::v-deep .el-form-item__label {
		padding-bottom: 8px;
		line-height: 20px;
		font-weight: 500;
	}
	::v-deep .el-form-item__content {
		min-height: 32px;
	}
}
.third-measure-section + .third-measure-section {
	margin-top: 8px;
}
.third-measure-section-title {
	margin-bottom: 16px;
	padding: 8px 0;
	line-height: 20px;
	font-size: 14px;
	font-weight: 600;
	color: #303133;
	border-bottom: 1px solid #ebeef5;
}
.third-measure-grid {
	flex-wrap: wrap;
}
</style>
